/**
 * Funções Auxiliares
 *
 * Funções utilitárias gerais para manipulação de dados,
 * geração de valores, operações assíncronas, etc.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Combina classes do Tailwind de forma inteligente
 * Resolve conflitos de classes e permite condicionais
 *
 * @param inputs - Classes a serem combinadas
 * @returns String de classes combinadas
 *
 * @example
 * cn('px-4 py-2', 'px-6') // 'py-2 px-6'
 * cn('text-red-500', isActive && 'text-green-500')
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Gera um ID único
 *
 * @returns String com ID único
 */
export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/**
 * Gera um UUID v4
 *
 * @returns String com UUID
 */
export function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Cria um delay/sleep
 *
 * @param ms - Tempo em milissegundos
 * @returns Promise que resolve após o tempo
 *
 * @example
 * await sleep(1000); // Aguarda 1 segundo
 */
export function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Debounce de função
 * Aguarda o tempo especificado sem novas chamadas antes de executar
 *
 * @param fn - Função a ser executada
 * @param delay - Tempo de espera em ms
 * @returns Função com debounce
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>;

  return function (...args: Parameters<T>) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Throttle de função
 * Limita a execução a uma vez por intervalo de tempo
 *
 * @param fn - Função a ser executada
 * @param limit - Intervalo mínimo entre execuções em ms
 * @returns Função com throttle
 */
export function throttle<T extends (...args: unknown[]) => unknown>(
  fn: T,
  limit: number,
): (...args: Parameters<T>) => void {
  let inThrottle = false;

  return function (...args: Parameters<T>) {
    if (!inThrottle) {
      fn(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Deep clone de objeto
 *
 * @param obj - Objeto a ser clonado
 * @returns Clone profundo do objeto
 */
export function deepClone<T>(obj: T): T {
  return JSON.parse(JSON.stringify(obj));
}

/**
 * Compara dois objetos para igualdade profunda
 *
 * @param obj1 - Primeiro objeto
 * @param obj2 - Segundo objeto
 * @returns true se iguais
 */
export function deepEqual(obj1: unknown, obj2: unknown): boolean {
  return JSON.stringify(obj1) === JSON.stringify(obj2);
}

/**
 * Remove propriedades undefined e null de um objeto
 *
 * @param obj - Objeto a ser limpo
 * @returns Objeto sem valores undefined/null
 */
export function removeNullish<T extends Record<string, unknown>>(
  obj: T,
): Partial<T> {
  const result: Partial<T> = {};

  for (const key in obj) {
    if (obj[key] !== null && obj[key] !== undefined) {
      result[key] = obj[key];
    }
  }

  return result;
}

/**
 * Agrupa array por uma chave
 *
 * @param array - Array a ser agrupado
 * @param key - Chave para agrupamento
 * @returns Objeto com arrays agrupados
 *
 * @example
 * groupBy([{type: 'a', v: 1}, {type: 'b', v: 2}, {type: 'a', v: 3}], 'type')
 * // { a: [{type: 'a', v: 1}, {type: 'a', v: 3}], b: [{type: 'b', v: 2}] }
 */
export function groupBy<T extends Record<string, unknown>>(
  array: T[],
  key: keyof T,
): Record<string, T[]> {
  return array.reduce(
    (result, item) => {
      const groupKey = String(item[key]);
      if (!result[groupKey]) {
        result[groupKey] = [];
      }
      result[groupKey].push(item);
      return result;
    },
    {} as Record<string, T[]>,
  );
}

/**
 * Ordena array de objetos por uma chave
 *
 * @param array - Array a ser ordenado
 * @param key - Chave para ordenação
 * @param order - Direção (asc ou desc)
 * @returns Array ordenado
 */
export function sortBy<T extends Record<string, unknown>>(
  array: T[],
  key: keyof T,
  order: 'asc' | 'desc' = 'asc',
): T[] {
  return [...array].sort((a, b) => {
    const aVal = a[key];
    const bVal = b[key];

    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });
}

/**
 * Remove duplicatas de array
 *
 * @param array - Array com possíveis duplicatas
 * @param key - Chave para comparação (opcional, para objetos)
 * @returns Array sem duplicatas
 */
export function unique<T>(array: T[], key?: keyof T): T[] {
  if (!key) {
    return [...new Set(array)];
  }

  const seen = new Set();
  return array.filter(item => {
    const value = (item as Record<string, unknown>)[key as string];
    if (seen.has(value)) return false;
    seen.add(value);
    return true;
  });
}

/**
 * Divide array em chunks de tamanho especificado
 *
 * @param array - Array a ser dividido
 * @param size - Tamanho de cada chunk
 * @returns Array de chunks
 */
export function chunk<T>(array: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }
  return chunks;
}

/**
 * Converte query string para objeto
 *
 * @param queryString - Query string (com ou sem ?)
 * @returns Objeto com parâmetros
 */
export function parseQueryString(queryString: string): Record<string, string> {
  const params = new URLSearchParams(queryString.replace(/^\?/, ''));
  const result: Record<string, string> = {};

  params.forEach((value, key) => {
    result[key] = value;
  });

  return result;
}

/**
 * Converte objeto para query string
 *
 * @param params - Objeto de parâmetros
 * @returns Query string
 */
export function toQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined && value !== '') {
      searchParams.append(key, String(value));
    }
  }

  return searchParams.toString();
}

/**
 * Copia texto para a área de transferência
 *
 * @param text - Texto a ser copiado
 * @returns Promise que resolve quando copiado
 */
export async function copyToClipboard(text: string): Promise<void> {
  if (navigator.clipboard) {
    await navigator.clipboard.writeText(text);
  } else {
    // Fallback para navegadores antigos
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }
}

/**
 * Download de arquivo a partir de Blob
 *
 * @param blob - Blob do arquivo
 * @param filename - Nome do arquivo
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}

/**
 * Verifica se está em ambiente de desenvolvimento
 *
 * @returns true se desenvolvimento
 */
export function isDev(): boolean {
  return import.meta.env.DEV;
}

/**
 * Verifica se está em ambiente de produção
 *
 * @returns true se produção
 */
export function isProd(): boolean {
  return import.meta.env.PROD;
}

/**
 * Dia AAAA-MM-DD de `date` no fuso do navegador.
 *
 * Use no lugar de `date.toISOString().split('T')[0]`, que devolve o dia em UTC
 * (depois das 21h em Brasília já é o dia seguinte). O backend interpreta
 * AAAA-MM-DD como o dia inteiro no fuso da loja.
 *
 * @example
 * localDateKey() // dia de hoje, ex.: '2026-10-05'
 * localDateKey(new Date(2026, 0, 31)) // '2026-01-31'
 */
export function localDateKey(date: Date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Dia AAAA-MM-DD de uma DATA DE CALENDÁRIO vinda da API (vencimento, data da
 * despesa, atestado...). O backend grava essas datas ao meio-dia UTC (e colunas
 * `date` do banco voltam à meia-noite UTC), então o dia certo é a parte de data
 * em UTC — não use o fuso do navegador, que mostraria o dia anterior para 00:00Z.
 */
export function calendarDateKey(value: string | Date): string {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const d = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

/**
 * Formata (dd/mm/aaaa) uma DATA DE CALENDÁRIO vinda da API (ver calendarDateKey).
 */
export function formatCalendarDate(value: string | Date | null | undefined): string {
  if (!value) return '';
  const key = calendarDateKey(value);
  if (!key) return '';
  const [y, m, d] = key.split('-');
  return `${d}/${m}/${y}`;
}

/**
 * Valor para `<input type="datetime-local">` (AAAA-MM-DDTHH:mm) de um INSTANTE,
 * na hora local do navegador. Não use `toISOString().slice(0, 16)`, que mostra a
 * hora UTC (3h adiantada em Brasília).
 */
export function toDateTimeLocalValue(value: string | Date | null | undefined): string {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${localDateKey(d)}T${hh}:${mm}`;
}

/**
 * Converte o valor de um `<input type="datetime-local">` (hora local do navegador,
 * sem fuso) no instante ISO 8601 (UTC) que o backend grava. Vazio → null.
 */
export function dateTimeLocalToIso(value: string | null | undefined): string | null {
  if (!value) return null;
  // `new Date('AAAA-MM-DDTHH:mm')` interpreta a string na hora local do navegador
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

/**
 * Converte um atalho de período em datas startDate/endDate ISO.
 *
 * @param range - Atalho de período ('today', 'yesterday', 'week', 'month')
 * @returns Objeto com startDate e endDate em formato ISO
 *
 * @example
 * resolveDateRange('today') // { startDate: '2024-01-01T...', endDate: '2024-01-01T...' }
 * resolveDateRange('custom') // {}
 */
export function resolveDateRange(range: string): { startDate?: string; endDate?: string } {
  if (!range || range === 'custom') return {};

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let start: Date;
  let end: Date = new Date(today.getTime() + 24 * 60 * 60 * 1000 - 1);

  switch (range) {
    case 'today':
      start = today;
      break;
    case 'yesterday': {
      start = new Date(today.getTime() - 24 * 60 * 60 * 1000);
      end = new Date(today.getTime() - 1);
      break;
    }
    case 'week': {
      const dayOfWeek = today.getDay();
      start = new Date(today.getTime() - dayOfWeek * 24 * 60 * 60 * 1000);
      break;
    }
    case 'month':
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    default:
      return {};
  }

  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

/**
 * Gera cor aleatória em hexadecimal
 *
 * @returns String com cor hex
 */
export function randomColor(): string {
  return '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
}

/**
 * Gera cor baseada em string (sempre a mesma cor para a mesma string)
 *
 * @param str - String base
 * @returns String com cor hex
 */
export function stringToColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';
  for (let i = 0; i < 3; i++) {
    const value = (hash >> (i * 8)) & 0xff;
    color += ('00' + value.toString(16)).slice(-2);
  }

  return color;
}

/**
 * Monta a URL de um arquivo enviado (ex.: "/files/123.pdf") no domínio da API.
 * Em produção a API fica em outro subdomínio (VITE_API_URL absoluta); no
 * desenvolvimento, o Vite encaminha /files para a API.
 */
export function resolveFileUrl(fileUrl: string): string {
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
  const apiUrl = import.meta.env.VITE_API_URL || '';
  try {
    const origin = new URL(apiUrl).origin;
    return `${origin}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
  } catch {
    return fileUrl;
  }
}

/** Texto do servidor que demorou a responder (a ação pode ter sido concluída) */
export const API_TIMEOUT_MESSAGE =
  'O servidor demorou para responder. Confira se a ação foi concluída antes de tentar de novo.';
/** Texto da falta de conexão com o servidor */
export const API_NETWORK_MESSAGE =
  'Não foi possível falar com o servidor. Confira a sua internet e tente de novo.';

/**
 * Mensagem em português para o erro do axios sem resposta do servidor (#272).
 * null = não é erro de rede. O texto já trocado pelo interceptador do `api` vale como
 * está. ECONNABORTED também é usado para "Request aborted" (o navegador cancelou, ao
 * sair da tela): só é tempo esgotado com o texto de timeout do axios.
 */
export function friendlyNetworkError(error: { code?: string; message?: string }): string | null {
  const message = error.message || '';
  if (message === API_TIMEOUT_MESSAGE || message === API_NETWORK_MESSAGE) return message;
  if (error.code === 'ETIMEDOUT' || /^timeout of \d+ms exceeded$/i.test(message)) return API_TIMEOUT_MESSAGE;
  if (error.code === 'ERR_NETWORK' || message === 'Network Error') return API_NETWORK_MESSAGE;
  return null;
}

/** Requisição cancelada pelo navegador ou pela tela (não há erro para mostrar) */
function isCanceledRequest(error: { code?: string; message?: string }): boolean {
  return error.code === 'ERR_CANCELED' || /^(canceled|Request aborted)$/i.test(error.message || '');
}

/**
 * Mensagem de erro de uma chamada à API: usa a mensagem do servidor
 * (response.data.error.message) quando houver; sem resposta do servidor, o aviso de
 * tempo esgotado ou de falta de rede em português; senão, o texto padrão.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const e = error as {
    response?: { data?: { error?: { message?: unknown }; message?: unknown } };
  };
  const serverMessage = e?.response?.data?.error?.message ?? e?.response?.data?.message;
  if (typeof serverMessage === 'string' && serverMessage.trim()) return serverMessage;
  if (!e?.response && error instanceof Error) {
    // Sem resposta do servidor (axios): texto em português, nunca o do axios em inglês
    const friendly = friendlyNetworkError(error as { code?: string; message?: string });
    if (friendly) return friendly;
    if (isCanceledRequest(error as { code?: string; message?: string })) return fallback;
    // Erro lançado pelo próprio front com mensagem legível
    if (error.message && !/status code/i.test(error.message)) return error.message;
  }
  return fallback;
}
